import { useState } from "react";
import ReferenceForm from "./ReferenceForm";
import { REFERENCE_TARGETS } from "./referenceHelpers";

/**
 * Applies one reference to the chosen values of every selected factor in one
 * request, or removes it. A factor without a value (no GWPs, no NCV, no
 * density) keeps that reference empty, so mixed selections are safe. A
 * reference missing from the library can be added here and is picked at once.
 *
 * @param {object} props
 * @param {number} props.count factors the change reaches
 * @param {boolean} props.allMatching every factor the filters match is selected
 * @param {number} props.totalFactors every factor the filters match
 * @param {import("./referencesApi").GhgReference[]} props.references
 * @param {() => void} props.onSelectAllMatching
 * @param {() => void} props.onClear
 * @param {(referenceId: number|null, targets: string[]) => Promise<boolean>} props.onApply true once saved, after which the caller clears the selection and so closes the bar
 * @param {() => void} props.onReferencesChanged
 * @param {{ target: string, label: string }[]} [props.targets] the values a reference can go on; emission factor values by default
 * @param {string[]} [props.initialTargets] the values ticked at first
 */
export default function ReferenceApplyBar({
  count,
  allMatching,
  totalFactors,
  references,
  onSelectAllMatching,
  onClear,
  onApply,
  onReferencesChanged,
  targets: targetOptions = REFERENCE_TARGETS,
  initialTargets = ["ef"],
}) {
  const [targets, setTargets] = useState(() => new Set(initialTargets));
  const [referenceId, setReferenceId] = useState("");
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);

  const toggleTarget = (target) =>
    setTargets((current) => {
      const next = new Set(current);
      if (next.has(target)) next.delete(target);
      else next.add(target);
      return next;
    });

  const run = async (id) => {
    setSaving(true);
    const saved = await onApply(id, [...targets]);
    if (!saved) setSaving(false);
  };

  const targetLabels = () => targetOptions.filter(({ target }) => targets.has(target)).map(({ label }) => label);

  const apply = () => {
    if (allMatching) {
      const title = references.find((reference) => String(reference.id) === referenceId)?.title;
      const question = `Apply "${title}" as the ${targetLabels().join(", ")} reference of all ${count.toLocaleString("en-IN")} factor(s) matching the filters? It replaces the reference they have now and cannot be undone.`;
      if (!window.confirm(question)) return;
    }
    run(Number(referenceId));
  };

  const remove = () => {
    if (window.confirm(`Remove the ${targetLabels().join(", ")} reference from ${count} factor(s)?`)) run(null);
  };

  const created = (reference) => {
    setCreating(false);
    setReferenceId(String(reference.id));
    onReferencesChanged();
  };

  const noTargets = targets.size === 0;

  return (
    <div className="efb-applybar" role="region" aria-label="Apply a reference to the selected factors">
      <div className="efb-applybar-line">
        <strong>
          {count.toLocaleString("en-IN")} factor{count === 1 ? "" : "s"} selected
        </strong>
        {!allMatching && totalFactors > count ? (
          <button type="button" className="kgb-btn kgb-btn--quiet" onClick={onSelectAllMatching}>
            Select all {totalFactors.toLocaleString("en-IN")} matching the filters
          </button>
        ) : null}
        <button type="button" className="kgb-btn kgb-btn--quiet" onClick={onClear}>
          Clear selection
        </button>
      </div>

      <div className="efb-applybar-line">
        <span className="efb-muted">Values</span>
        {targetOptions.map(({ target, label }) => (
          <label key={target} className="efb-check">
            <input type="checkbox" checked={targets.has(target)} onChange={() => toggleTarget(target)} />
            {label}
          </label>
        ))}
      </div>

      <div className="efb-applybar-line">
        <label className="efb-field efb-field--grow">
          <span>Reference</span>
          <select value={referenceId} onChange={(e) => setReferenceId(e.target.value)}>
            <option value="">Pick a reference…</option>
            {references.map((reference) => (
              <option key={reference.id} value={String(reference.id)}>
                {reference.title}
                {reference.note ? ` — ${reference.note}` : ""}
              </option>
            ))}
          </select>
        </label>
        {creating ? null : (
          <button type="button" className="kgb-btn kgb-btn--quiet" onClick={() => setCreating(true)}>
            + New reference
          </button>
        )}
        <button
          type="button"
          className="kgb-btn kgb-btn--primary"
          disabled={saving || noTargets || !referenceId}
          onClick={apply}
        >
          {saving ? "Saving…" : "Apply"}
        </button>
        <button type="button" className="kgb-btn kgb-btn--danger" disabled={saving || noTargets} onClick={remove}>
          Remove reference
        </button>
      </div>

      {creating ? <ReferenceForm onSaved={created} onCancel={() => setCreating(false)} /> : null}
    </div>
  );
}
